import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider } from '@material-ui/core/styles';
import { Provider as ReduxProvider } from 'react-redux';
import { ErrorBoundary, Provider as RollbarProvider } from '@rollbar/react';
import { get } from 'lodash';
import App, { AppContext, AppInitialProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import React from 'react';
import { IntercomProvider } from 'react-use-intercom';
import Rollbar from 'rollbar';
import { TypeError } from '../src/components/layouts/ErrorLayouts';
import IntercomConfig from '../src/config/intercom';
import rollbar from '../src/config/rollbar';
import theme from '../src/theme';
import { UserProvider } from '@auth0/nextjs-auth0/client';
// global css imports
import 'flatpickr/dist/themes/material_blue.css';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import '../src/styles/month-select-datepicker.scss';
import '../src/styles/style.scss';
// Gutenberg editor styles
import '@wordpress/components/build-style/style.css';
import '@wordpress/block-editor/build-style/style.css';
import '@wordpress/block-library/build-style/style.css';
import '../src/styles/content-recommendations/gutenberg-comments.scss';
// Content Index page styles
import '../src/styles/content-index.scss';
import '../src/components/content-index-manager/ContentIndexV3.scss';
import { APP_STORE } from '../src/utility/store/redux';
import InitGTMView from '../src/components/InitGTMView';
import { cancelPendingAmplitudePageview, installWindowAnalyticsShim } from '../src/constants/analyticsMeta';
import xss from 'xss';

const MetaTagGenerator = dynamic(import('../src/components/shared/MetaTagGenerator'));
const DefaultLayout = dynamic(import('../src/components/layouts/DefaultLayout'));

interface WithRollbarProps {
  enabled: boolean;
  rollbarConfig: Rollbar.Configuration;
  children?: React.ReactElement | React.ReactElement[];
}

/**
 * There is no theme + CSS when the Fallback UI is called, so we have to reconfigured that here
 *
 */
const FallbackUI: React.FunctionComponent = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TypeError />
    </ThemeProvider>
  );
};

/**
 * Higher-order component wrapper to add Rollbar, conditionally
 */
const WithRollbar: React.FunctionComponent<WithRollbarProps> = ({ enabled, rollbarConfig, children }) => {
  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <RollbarProvider config={rollbarConfig}>
      <ErrorBoundary fallbackUI={FallbackUI}>{children}</ErrorBoundary>
    </RollbarProvider>
  );
};

interface MyAppProps {
  isRollbarEnabled: boolean;
  rollbarConfig: Rollbar.Configuration;
  // reduxStore: StoreType;
}

// Function to sanitize query parameters
const sanitizeParam = (param: string) => {
  return xss(param); // Using the xss library for sanitization
};

const validateQueryParam = (param: string): boolean => {
  const unsafePattern = /<.*script.*>|javascript:|onerror=|<.*>.*<\/.*>/i; // JS/HTML code invalid URL
  // Check if the parameter matches the allowed safe pattern and is not unsafe
  // return safePattern.test(param) && !unsafePattern.test(param);
  return !unsafePattern.test(param); ////TEMP: just blocking for JS related query param
};
class MyApp extends App<MyAppProps> {
  constructor(props) {
    super(props);
    this.state = {
      sanitizedParams: {},
      error: null,
    };
  }

  componentDidMount() {
    try {
      installWindowAnalyticsShim();
      window.analytics?.page();
      Router.events.on('routeChangeComplete', this.handleRouteChangeComplete);

      const { query } = this.props.router; // Get query parameters from the router passed by withRouter

      const sanitizedQuery = {};
      const invalidParams: string[] = [];

      for (const queryParamKey in query) {
        const paramValue: string = (
          Array.isArray(query?.[queryParamKey]) ? query?.[queryParamKey]?.[0] : query?.[queryParamKey]
        ) as string;
        const sanitizedValue = sanitizeParam(paramValue);

        // Validate the sanitized query parameter
        if (validateQueryParam(sanitizedValue)) {
          sanitizedQuery[queryParamKey] = sanitizedValue;
        } else {
          invalidParams.push(queryParamKey);
        }
      }

      // If there are invalid query parameters, show an error
      if (invalidParams.length > 0) {
        this.setState({
          error: `Invalid query parameters: ${invalidParams?.join(', ')}`,
        });
      } else {
        this.setState({
          sanitizedParams: sanitizedQuery,
          error: null,
        });
      }
    } catch (error) {
      console.error('Error during query parameter sanitization:', error);
      this.setState({
        error: 'An error occurred while processing query parameters.',
      });
    }
  }

  componentWillUnmount() {
    cancelPendingAmplitudePageview();
    Router.events.off('routeChangeComplete', this.handleRouteChangeComplete);
  }

  // Next passes (url, { shallow }). A SHALLOW route change is not navigation: it is this app
  // syncing component state into the URL (AI Visibility filters/platform/tab, both tracker
  // wizards' step, drilldown scope) via router.replace(..., { shallow: true }). Those were each
  // emitting a Page Viewed, so a single session of filtering looked like dozens of page views and
  // any Page Viewed comparison between screens measured URL churn rather than usage. The severity
  // is on record in useAivViewState's own guard, which exists because ~90 replaces/sec were
  // observed on the drilldown. Real navigations (push, non-shallow replace, back/forward) are
  // unaffected, and the state changes that DO matter now emit their own explicit events.
  handleRouteChangeComplete = (_url?: string, options?: { shallow?: boolean }) => {
    if (options?.shallow) return;
    window.analytics?.page();
  };

  render() {
    const { error, sanitizedParams } = this.state as any;

    if (error) {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <TypeError errorMessage={error}></TypeError>
        </ThemeProvider>
      );
    }
    type PagePropsType = {
      featureFlags?: Record<string, unknown>;
      features: Record<string, unknown>;
    };
    const { Component } = this.props;
    // props.initialReduxState);

    // TODO double check existing legacy JS
    const pageProps = this.props.pageProps as PagePropsType;

    // @ts-expect-error TODO this is existing JS code, I believe it always was undefined and went to the Default Layout
    const Layout = Component.Layout || DefaultLayout;
    let { featureFlags } = pageProps as PagePropsType;
    // when user sign in and comes to dashboard then we get featureflow data in features key
    if (!(featureFlags && Object.keys(featureFlags).length)) {
      featureFlags = pageProps.features;
    }
    return (
      <WithRollbar enabled={this.props.isRollbarEnabled} rollbarConfig={this.props.rollbarConfig}>
        <ReduxProvider store={APP_STORE}>
          <UserProvider>
            <InitGTMView />
            <Head>
              <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />
            </Head>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <IntercomProvider appId={IntercomConfig.appId()} autoBoot={true}>
                <Layout featureFlags={featureFlags}>
                  {!get(Router, 'router.query.slug') ? <MetaTagGenerator /> : null}
                  <Component {...pageProps} />
                </Layout>
              </IntercomProvider>
            </ThemeProvider>
          </UserProvider>
        </ReduxProvider>
      </WithRollbar>
    );
  }

  static getInitialProps: (appContext: AppContext) => Promise<AppInitialProps> = async (appContext) => {
    const parentAppProps = await App.getInitialProps(appContext);
    const appProps: MyAppProps = {
      isRollbarEnabled: rollbar.isRollbarEnabled(),
      rollbarConfig: rollbar.rollbarConfig(),
      // reduxStore,
    };

    return { ...parentAppProps, ...appProps };
  };

  // static propTypes = {
  //   reduxStore: propTypes.object.isRequired,
  // };
}

export default MyApp;
